import React from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import useBadgeToast from "@/components/site/BadgeToast";
import ShareReactBar from "@/components/site/ShareReactBar";

export default function ConsultationDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const showBadges = useBadgeToast();
  const [data, setData] = React.useState(null);
  const [answer, setAnswer] = React.useState("");
  const [extra, setExtra] = React.useState("");
  const [age, setAge] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const load = React.useCallback(() => {
    api.get(`/consultations/${slug}`).then((r) => setData(r.data));
  }, [slug]);

  React.useEffect(() => { load(); }, [load]);

  // If the consultation has an external_url, redirect away (in case someone lands here by bookmark/share)
  React.useEffect(() => {
    if (data?.consultation?.external_url) {
      window.location.replace(data.consultation.external_url);
    }
  }, [data]);

  if (!data) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">Chargement...</div>;
  if (data.consultation?.external_url) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="consult-external-redirect">
        <p>Redirection vers le formulaire externe…</p>
        <a href={data.consultation.external_url} className="neo-btn bg-cef-pink text-white mt-4 inline-flex" target="_blank" rel="noreferrer">Ouvrir maintenant ↗</a>
      </div>
    );
  }
  const c = data.consultation;
  const total = data.total || 0;
  const isOpen = c.type === "boite_idees" || c.type === "temoignage";

  const submit = async (e) => {
    e?.preventDefault();
    const value = isOpen ? extra.trim() : answer;
    if (!value) { toast.error("Sélectionne une réponse ou écris ton avis."); return; }
    try {
      const { data: res } = await api.post(`/consultations/${slug}/respond`, {
        answer: isOpen ? "libre" : answer,
        extra_text: extra || null,
        age_range: age || (user?.age ? `${user.age}` : null),
      });
      toast.success("Merci, ta réponse compte ! 💪");
      if (res.new_badges?.length) showBadges(res.new_badges);
      setSubmitted(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/donner-mon-avis" className="inline-flex items-center gap-2 text-sm font-bold hover:underline mb-6" data-testid="back-to-consults"><ArrowLeft className="w-4 h-4" /> Consultations</Link>
      <span className="chip bg-cef-yellow">{c.type.replace("_", " ")}</span>
      <h1 className="mt-3 font-display font-black text-3xl sm:text-5xl tracking-tighter">{c.title}</h1>
      <p className="mt-3 text-neutral-700">{c.description}</p>

      {!user && (
        <div className="mt-4 neo-card p-4 bg-cef-yellow text-sm" data-testid="login-tip">
          💡 Tu peux voter sans compte. Mais avec un compte, tu gagnes des badges ! <Link to="/login" className="font-bold underline">Connexion</Link> ou <Link to="/signup" className="font-bold underline">inscription</Link>.
        </div>
      )}

      {!submitted && c.status === "en_cours" && (
        <form onSubmit={submit} className="mt-6 neo-card p-6 bg-white" data-testid="consult-form">
          {!isOpen && c.options?.length > 0 && (
            <div className="grid gap-2">
              {c.options.map((opt) => (
                <label key={opt} className={`flex items-center gap-3 p-3 border-2 border-black rounded-md cursor-pointer transition ${answer === opt ? "bg-cef-blue text-white shadow-brutal" : "bg-white hover:bg-cef-yellow"}`} data-testid={`opt-${opt}`}>
                  <input type="radio" className="sr-only" name="opt" value={opt} checked={answer === opt} onChange={() => setAnswer(opt)} />
                  <span className="w-5 h-5 border-2 border-black inline-flex items-center justify-center rounded bg-white">{answer === opt && <Check className="w-3 h-3 text-black" />}</span>
                  <span className="font-bold">{opt}</span>
                </label>
              ))}
            </div>
          )}
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder={isOpen ? "Écris ici ton idée ou ton témoignage..." : "Tu veux préciser ? (optionnel)"}
            className="neo-input mt-4 min-h-[120px]"
            data-testid="consult-textarea"
          />
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <select value={age} onChange={(e) => setAge(e.target.value)} className="neo-input" data-testid="age-select">
              <option value="">Ton âge (optionnel)</option>
              <option value="12-14">12-14 ans</option>
              <option value="15-17">15-17 ans</option>
              <option value="18-20">18-20 ans</option>
            </select>
            <button type="submit" className="neo-btn bg-cef-pink text-white" data-testid="submit-consult-btn">Envoyer ma réponse</button>
          </div>
        </form>
      )}

      {submitted && <div className="mt-8 neo-card p-6 bg-cef-green" data-testid="thanks-msg">Merci pour ta participation ! Tu peux maintenant voir les résultats.</div>}

      {!isOpen && total > 0 && (
        <div className="mt-10">
          <h2 className="font-display font-black text-2xl tracking-tighter uppercase mb-4">Résultats en direct ({total})</h2>
          <div className="space-y-3">
            {c.options.map((opt) => {
              const v = data.results?.[opt] || 0;
              const pct = total ? Math.round((v / total) * 100) : 0;
              return (
                <div key={opt} className="neo-card p-4">
                  <div className="flex justify-between text-sm font-bold mb-2"><span>{opt}</span><span>{pct}% ({v})</span></div>
                  <div className="h-4 bg-white border-2 border-black rounded-sm overflow-hidden">
                    <div className="h-full bg-cef-blue" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {c.follow_up && (
        <div className="mt-10 neo-card p-5 bg-cef-yellow">
          <div className="font-bold uppercase text-xs mb-1">Que se passe-t-il ensuite ?</div>
          <p className="text-sm">{c.follow_up}</p>
        </div>
      )}

      <ShareReactBar title={c.title} kind="Consultation" />
    </div>
  );
}
